import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, TrendingUp, Users, Globe, Cpu, DollarSign, Loader2, Search, ChevronRight, BarChart3, Shield, Activity, Settings } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const StatCard = ({ label, value, sub, color, icon: Icon }) => (
  <div style={{ padding: '16px', border: '1px solid var(--faint)', background: 'rgba(242,239,233,0.02)' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
      <span style={{ fontFamily: 'var(--mono)', fontSize: '8px', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--muted)' }}>{label}</span>
      {Icon && <Icon size={12} style={{ color: color || 'var(--gold)', opacity: 0.6 }} />}
    </div>
    <p style={{ fontFamily: 'var(--serif)', fontSize: '24px', color: color || 'var(--fg)', marginBottom: '2px' }}>{value}</p>
    {sub && <p style={{ fontFamily: 'var(--mono)', fontSize: '8px', color: 'var(--muted)' }}>{sub}</p>}
  </div>
);

const SimpleBar = ({ items, maxVal, color = 'var(--gold)' }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
    {items.map((item, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: '9px', color: 'var(--muted)', width: '100px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', flexShrink: 0 }}>{item.label}</span>
        <div style={{ flex: 1, height: '16px', background: 'rgba(242,239,233,0.03)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${Math.max(2, (item.value / (maxVal || 1)) * 100)}%`, background: color, transition: 'width .5s' }} />
        </div>
        <span style={{ fontFamily: 'var(--mono)', fontSize: '9px', color: 'var(--fg)', width: '50px', textAlign: 'right', flexShrink: 0 }}>{item.display || item.value}</span>
      </div>
    ))}
  </div>
);

const NAV_ITEMS = [
  { key: 'overview', label: 'Overview', icon: BarChart3 },
  { key: 'revenue', label: 'Revenue', icon: DollarSign },
  { key: 'costs', label: 'AI Costs', icon: Cpu },
  { key: 'users', label: 'Users', icon: Users },
  { key: 'sites', label: 'Sites', icon: Globe },
  { key: 'alerts', label: 'Alerts', icon: Shield },
  { key: 'activity', label: 'Activity', icon: Activity },
];

const AdminDashboard = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('overview');
  const [usersList, setUsersList] = useState(null);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersPlan, setUsersPlan] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/admin/dashboard`, { withCredentials: true });
      setData(res.data);
    } catch (err) { setError(err.response?.data?.detail || 'Access denied'); }
    setLoading(false);
  };

  const fetchUsers = async (search = '', plan = '') => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (plan) params.set('plan', plan);
      const res = await axios.get(`${API}/admin/users?${params}`, { withCredentials: true });
      setUsersList(res.data);
    } catch { /* error */ }
  };

  const fetchUserDetail = async (userId) => {
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`, { withCredentials: true });
      setUserDetail(res.data);
      setSelectedUser(userId);
    } catch { /* error */ }
    setLoadingDetail(false);
  };

  useEffect(() => { if (tab === 'users' && !usersList) fetchUsers(); }, [tab]);

  if (loading) return (
    <div className="app-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Loader2 size={20} className="animate-spin" style={{ color: 'var(--gold)' }} />
    </div>
  );

  if (error) return (
    <div className="app-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px' }}>
      <AlertTriangle size={28} style={{ color: '#c47272' }} />
      <p style={{ color: '#c47272', fontSize: '14px' }}>{error}</p>
      <button onClick={() => navigate('/dashboard')} className="btn-outline">Back to Dashboard</button>
    </div>
  );

  const r = data?.revenue || {};
  const c = data?.costs || {};
  const s = data?.sites || {};
  const a = data?.activity || {};
  const al = data?.alerts || {};
  const tr = data?.trends || {};

  return (
    <div style={{ display: 'flex', height: '100vh', background: 'var(--bg)', color: 'var(--fg)' }}>
      {/* ── SIDEBAR ── */}
      <div style={{ width: '200px', borderRight: '1px solid var(--faint)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ padding: '16px', borderBottom: '1px solid var(--faint)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
            <a href="/" style={{ fontFamily: 'var(--serif)', fontSize: '16px', color: 'var(--fg)', textDecoration: 'none', letterSpacing: '-.02em' }}>
              golden<em style={{ fontStyle: 'italic', color: 'var(--gold)' }}>hour</em>
            </a>
            <span style={{ fontFamily: 'var(--mono)', fontSize: '7px', letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(242,239,233,.2)' }}>by Uncut Gems</span>
          </div>
          <span style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: '8px', color: 'var(--gold)', letterSpacing: '.12em', marginTop: '4px' }}>ADMIN</span>
        </div>
        <nav style={{ flex: 1, padding: '8px 0' }}>
          {NAV_ITEMS.map(item => (
            <button key={item.key} onClick={() => { setTab(item.key); setSelectedUser(null); }} data-testid={`admin-nav-${item.key}`} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px',
              background: tab === item.key ? 'rgba(201,168,76,0.06)' : 'transparent',
              border: 'none', cursor: 'pointer', color: tab === item.key ? 'var(--gold)' : 'var(--muted)',
              fontFamily: 'var(--sans)', fontSize: '12px', textAlign: 'left', transition: 'all .2s',
              borderLeft: tab === item.key ? '2px solid var(--gold)' : '2px solid transparent',
            }}>
              <item.icon size={14} />
              {item.label}
              {item.key === 'alerts' && al.alert_count > 0 && (
                <span style={{ marginLeft: 'auto', background: '#c47272', color: '#fff', fontSize: '9px', fontWeight: 700, padding: '1px 6px', borderRadius: '8px' }}>{al.alert_count}</span>
              )}
            </button>
          ))}
        </nav>
        <div style={{ padding: '12px 16px', borderTop: '1px solid var(--faint)' }}>
          <button onClick={() => navigate('/dashboard')} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'var(--mono)', fontSize: '9px', color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
            <ArrowLeft size={10} /> Back to App
          </button>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: '24px' }}>
            {selectedUser ? 'User Detail' : NAV_ITEMS.find(n => n.key === tab)?.label || 'Admin'}
          </h1>
          <button onClick={fetchDashboard} style={{ fontFamily: 'var(--mono)', fontSize: '9px', color: 'var(--muted)', background: 'none', border: '1px solid var(--faint)', padding: '6px 14px', cursor: 'pointer', letterSpacing: '.08em' }}>
            REFRESH
          </button>
        </div>

        {/* ── OVERVIEW TAB ── */}
        {tab === 'overview' && !selectedUser && (
          <>
            {al.alert_count > 0 && (
              <div style={{ padding: '10px 14px', background: 'rgba(200,150,50,0.06)', border: '1px solid rgba(200,150,50,0.15)', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => setTab('alerts')}>
                <AlertTriangle size={13} style={{ color: '#c89632' }} />
                <span style={{ fontSize: '12px' }}>{al.alert_count} alert{al.alert_count > 1 ? 's' : ''} need attention</span>
                <ChevronRight size={12} style={{ marginLeft: 'auto', color: 'var(--muted)' }} />
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '28px' }}>
              <StatCard label="MRR" value={`$${r.mrr?.toLocaleString() || 0}`} sub={`ARR: $${r.arr?.toLocaleString() || 0}`} icon={DollarSign} />
              <StatCard label="Paying Users" value={r.paying_users || 0} sub={`${r.conversion_rate || 0}% conversion`} icon={Users} />
              <StatCard label="AI Spend" value={`$${c.month_total_cost || 0}`} sub={`${c.month_total_calls || 0} calls this month`} icon={Cpu} />
              <StatCard label="Gross Margin" value={`$${c.gross_margin || 0}`} sub={`${c.margin_pct || 0}% margin`} color={c.margin_pct < 50 ? '#c47272' : '#5ba89d'} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '28px' }}>
              <StatCard label="Total Users" value={r.total_users || 0} sub={`${r.free_users || 0} free`} />
              <StatCard label="Total Sites" value={s.total_sites || 0} sub={`${s.published_sites || 0} published`} icon={Globe} />
              <StatCard label="Churn" value={`${r.churn_rate || 0}%`} sub={`${r.past_due || 0} past due`} color={r.churn_rate > 5 ? '#c47272' : undefined} />
              <StatCard label="Signups (7d)" value={a.signups_7d || 0} sub={`${a.signups_24h || 0} today`} icon={Activity} />
            </div>

            {/* Trend Charts */}
            {tr.months?.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '28px' }}>
                {[
                  { label: 'SIGNUPS', data: tr.signups, color: 'var(--gold)' },
                  { label: 'AI COST', data: tr.ai_cost, color: '#c47272', prefix: '$' },
                  { label: 'REVENUE', data: tr.revenue, color: '#5ba89d', prefix: '$' },
                ].map(chart => (
                  <div key={chart.label} style={{ padding: '16px', border: '1px solid var(--faint)' }}>
                    <h3 style={{ fontFamily: 'var(--mono)', fontSize: '8px', color: 'var(--muted)', letterSpacing: '.1em', marginBottom: '12px' }}>{chart.label} (6 MONTHS)</h3>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '60px' }}>
                      {chart.data?.map((val, i) => {
                        const max = Math.max(...(chart.data || [1])) || 1;
                        return (
                          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                            <div style={{ width: '100%', background: chart.color, height: `${Math.max(2, (val / max) * 50)}px`, opacity: i === chart.data.length - 1 ? 1 : 0.5, transition: 'height .5s' }} />
                            <span style={{ fontFamily: 'var(--mono)', fontSize: '7px', color: 'var(--muted)' }}>{tr.months[i]?.slice(5)}</span>
                          </div>
                        );
                      })}
                    </div>
                    <p style={{ fontFamily: 'var(--mono)', fontSize: '9px', color: chart.color, marginTop: '8px' }}>
                      Current: {chart.prefix || ''}{chart.data?.[chart.data.length - 1] || 0}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {c.by_type?.length > 0 && (
              <div style={{ marginBottom: '28px' }}>
                <h3 style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--muted)', letterSpacing: '.1em', marginBottom: '12px' }}>AI COST BREAKDOWN</h3>
                <SimpleBar
                  items={c.by_type.map(t => ({ label: t.type, value: t.cost, display: `$${t.cost}` }))}
                  maxVal={Math.max(...c.by_type.map(t => t.cost))}
                />
              </div>
            )}

            {s.popular_styles?.length > 0 && (
              <div>
                <h3 style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--muted)', letterSpacing: '.1em', marginBottom: '12px' }}>POPULAR STYLES</h3>
                <SimpleBar
                  items={s.popular_styles.map(st => ({ label: st.style || 'unknown', value: st.count, display: `${st.count}` }))}
                  maxVal={Math.max(...s.popular_styles.map(st => st.count))}
                  color="rgba(201,168,76,0.6)"
                />
              </div>
            )}
          </>
        )}

        {/* ── REVENUE TAB ── */}
        {tab === 'revenue' && !selectedUser && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '28px' }}>
              <StatCard label="MRR" value={`$${r.mrr?.toLocaleString() || 0}`} icon={DollarSign} />
              <StatCard label="ARR" value={`$${r.arr?.toLocaleString() || 0}`} icon={TrendingUp} />
              <StatCard label="ARPU" value={`$${r.arpu || 0}`} sub="Avg revenue per user" />
              <StatCard label="Churn" value={`${r.churn_rate || 0}%`} color={r.churn_rate > 5 ? '#c47272' : '#5ba89d'} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '28px' }}>
              <div style={{ padding: '20px', border: '1px solid var(--faint)' }}>
                <h3 style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--muted)', letterSpacing: '.1em', marginBottom: '16px' }}>PLAN BREAKDOWN</h3>
                <SimpleBar items={[
                  { label: 'Launch', value: r.launch_count || 0, display: `${r.launch_count || 0} users · $${r.launch_mrr || 0}/mo` },
                  { label: 'Pro', value: r.pro_count || 0, display: `${r.pro_count || 0} users · $${r.pro_mrr || 0}/mo` },
                  { label: 'Free', value: r.free_users || 0, display: `${r.free_users || 0} users · $0` },
                ]} maxVal={Math.max(r.launch_count || 0, r.pro_count || 0, r.free_users || 1)} />
              </div>
              <div style={{ padding: '20px', border: '1px solid var(--faint)' }}>
                <h3 style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--muted)', letterSpacing: '.1em', marginBottom: '16px' }}>UNIT ECONOMICS</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <StatCard label="Revenue" value={`$${r.mrr || 0}`} sub="Monthly" />
                  <StatCard label="AI Cost" value={`$${c.month_total_cost || 0}`} sub="Monthly" />
                  <StatCard label="Gross Profit" value={`$${c.gross_margin || 0}`} color={c.gross_margin >= 0 ? '#5ba89d' : '#c47272'} />
                  <StatCard label="Margin" value={`${c.margin_pct || 0}%`} color={c.margin_pct >= 70 ? '#5ba89d' : c.margin_pct >= 50 ? 'var(--gold)' : '#c47272'} />
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── AI COSTS TAB ── */}
        {tab === 'costs' && !selectedUser && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '28px' }}>
              <StatCard label="Total Spend" value={`$${c.month_total_cost || 0}`} sub="This month" icon={Cpu} />
              <StatCard label="Total Calls" value={c.month_total_calls || 0} sub="This month" />
              <StatCard label="Demo Spend" value={`$${c.demo_cost || 0}`} sub={`${c.demo_count || 0} generations`} />
              <StatCard label="Margin" value={`${c.margin_pct || 0}%`} sub={`$${c.gross_margin || 0} profit`} color={c.margin_pct >= 70 ? '#5ba89d' : '#c47272'} />
            </div>

            {c.by_type?.length > 0 && (
              <div style={{ marginBottom: '28px' }}>
                <h3 style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--muted)', letterSpacing: '.1em', marginBottom: '12px' }}>COST BY CALL TYPE</h3>
                <div style={{ border: '1px solid var(--faint)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 100px', padding: '8px 14px', borderBottom: '1px solid var(--faint)', background: 'rgba(242,239,233,0.02)' }}>
                    {['TYPE', 'COST', 'CALLS', 'AVG TIME'].map(h => (
                      <span key={h} style={{ fontFamily: 'var(--mono)', fontSize: '8px', color: 'var(--muted)', letterSpacing: '.1em', textAlign: h !== 'TYPE' ? 'right' : 'left' }}>{h}</span>
                    ))}
                  </div>
                  {c.by_type.map((t, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 100px', padding: '10px 14px', borderBottom: i < c.by_type.length - 1 ? '1px solid var(--faint)' : 'none' }}>
                      <span style={{ fontSize: '12px', color: 'var(--fg)' }}>{t.type}</span>
                      <span style={{ fontSize: '12px', color: 'var(--gold)', textAlign: 'right', fontFamily: 'var(--mono)' }}>${t.cost}</span>
                      <span style={{ fontSize: '12px', color: 'var(--muted)', textAlign: 'right' }}>{t.count}</span>
                      <span style={{ fontSize: '12px', color: 'var(--muted)', textAlign: 'right' }}>{t.avg_duration_ms > 0 ? `${(t.avg_duration_ms / 1000).toFixed(1)}s` : '—'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {c.top_users?.length > 0 && (
              <div>
                <h3 style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--muted)', letterSpacing: '.1em', marginBottom: '12px' }}>TOP USERS BY COST</h3>
                <div style={{ border: '1px solid var(--faint)' }}>
                  {c.top_users.map((u, i) => (
                    <div key={i} onClick={() => fetchUserDetail(u.user_id)} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 60px 60px', padding: '10px 14px', borderBottom: i < c.top_users.length - 1 ? '1px solid var(--faint)' : 'none', cursor: 'pointer', transition: 'background .2s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.04)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ fontSize: '11px', color: 'var(--fg)' }}>{u.email}</span>
                      <span style={{ fontSize: '11px', color: 'var(--gold)', textAlign: 'right', fontFamily: 'var(--mono)' }}>${u.cost}</span>
                      <span style={{ fontSize: '11px', color: 'var(--muted)', textAlign: 'right' }}>{u.calls}</span>
                      <span style={{ fontSize: '9px', color: 'var(--muted)', textAlign: 'right', fontFamily: 'var(--mono)', textTransform: 'uppercase' }}>{u.plan}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── USERS TAB ── */}
        {tab === 'users' && !selectedUser && (
          <>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
                <input type="text" value={usersSearch} onChange={e => setUsersSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && fetchUsers(usersSearch, usersPlan)}
                  placeholder="Search by email or name..."
                  data-testid="admin-user-search"
                  style={{ width: '100%', padding: '8px 10px 8px 32px', fontSize: '12px', fontFamily: 'var(--sans)', background: 'rgba(242,239,233,0.04)', border: '1px solid var(--faint)', color: 'var(--fg)', outline: 'none' }} />
              </div>
              <select value={usersPlan} onChange={e => { setUsersPlan(e.target.value); fetchUsers(usersSearch, e.target.value); }}
                style={{ padding: '8px 12px', fontSize: '11px', fontFamily: 'var(--mono)', background: 'rgba(242,239,233,0.04)', border: '1px solid var(--faint)', color: 'var(--fg)', cursor: 'pointer' }}>
                <option value="">All plans</option>
                <option value="free">Free</option>
                <option value="launch">Launch</option>
                <option value="pro">Pro</option>
              </select>
              <button onClick={() => fetchUsers(usersSearch, usersPlan)} style={{ padding: '8px 16px', fontFamily: 'var(--mono)', fontSize: '9px', background: 'var(--gold)', color: '#000', border: 'none', cursor: 'pointer', letterSpacing: '.06em' }}>SEARCH</button>
            </div>

            {usersList && (
              <>
                <p style={{ fontFamily: 'var(--mono)', fontSize: '9px', color: 'var(--muted)', marginBottom: '10px' }}>{usersList.total} users total</p>
                <div style={{ border: '1px solid var(--faint)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 60px 60px 80px 40px', padding: '8px 14px', borderBottom: '1px solid var(--faint)', background: 'rgba(242,239,233,0.02)' }}>
                    {['EMAIL', 'NAME', 'PLAN', 'SITES', 'AI COST', ''].map(h => (
                      <span key={h} style={{ fontFamily: 'var(--mono)', fontSize: '8px', color: 'var(--muted)', letterSpacing: '.1em' }}>{h}</span>
                    ))}
                  </div>
                  {usersList.users.map((u, i) => (
                    <div key={u.id} onClick={() => fetchUserDetail(u.id)} data-testid={`admin-user-row-${u.id}`} style={{
                      display: 'grid', gridTemplateColumns: '1fr 120px 60px 60px 80px 40px', padding: '10px 14px',
                      borderBottom: i < usersList.users.length - 1 ? '1px solid var(--faint)' : 'none',
                      cursor: 'pointer', transition: 'background .2s',
                    }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.04)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ fontSize: '11px', color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</span>
                      <span style={{ fontSize: '11px', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name || '—'}</span>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: '9px', color: u.plan === 'pro' ? 'var(--gold)' : u.plan === 'launch' ? '#5ba89d' : 'var(--muted)', textTransform: 'uppercase' }}>{u.plan || 'free'}</span>
                      <span style={{ fontSize: '11px', color: 'var(--muted)' }}>{u.site_count}</span>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: u.ai_cost_month > 0 ? 'var(--gold)' : 'var(--muted)' }}>${u.ai_cost_month}</span>
                      <ChevronRight size={12} style={{ color: 'var(--muted)' }} />
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* ── USER DETAIL VIEW ── */}
        {selectedUser && (
          <>
            <button onClick={() => { setSelectedUser(null); setUserDetail(null); }} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontFamily: 'var(--mono)', fontSize: '9px', color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '20px' }}>
              <ArrowLeft size={10} /> Back to {tab === 'users' ? 'Users' : 'list'}
            </button>

            {loadingDetail ? (
              <Loader2 size={20} className="animate-spin" style={{ color: 'var(--gold)' }} />
            ) : userDetail ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                  <div>
                    <h2 style={{ fontFamily: 'var(--serif)', fontSize: '20px', marginBottom: '2px' }}>{userDetail.user?.name || userDetail.user?.email}</h2>
                    <p style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--muted)' }}>{userDetail.user?.email}</p>
                  </div>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: '9px', padding: '4px 10px', letterSpacing: '.1em', textTransform: 'uppercase', background: userDetail.user?.plan === 'pro' ? 'rgba(201,168,76,0.1)' : 'rgba(242,239,233,0.04)', color: userDetail.user?.plan === 'pro' ? 'var(--gold)' : 'var(--muted)', border: `1px solid ${userDetail.user?.plan === 'pro' ? 'rgba(201,168,76,0.2)' : 'var(--faint)'}` }}>
                    {userDetail.user?.plan || 'free'}
                  </span>
                  {userDetail.user?.subscription_status && (
                    <span style={{ fontFamily: 'var(--mono)', fontSize: '9px', color: userDetail.user.subscription_status === 'active' ? '#5ba89d' : '#c47272' }}>
                      {userDetail.user.subscription_status}
                    </span>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '24px' }}>
                  <StatCard label="AI Cost (Month)" value={`$${userDetail.ai_cost_month}`} />
                  <StatCard label="AI Calls" value={userDetail.ai_calls_month} />
                  <StatCard label="Sites" value={userDetail.sites?.length || 0} />
                  <StatCard label="Edits Used" value={userDetail.edit_usage?.count || 0} sub={`of ${userDetail.user?.plan === 'pro' ? 'unlimited' : '30'}`} />
                </div>

                {/* User's sites */}
                {userDetail.sites?.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--muted)', letterSpacing: '.1em', marginBottom: '10px' }}>SITES</h3>
                    <div style={{ border: '1px solid var(--faint)' }}>
                      {userDetail.sites.map((site, i) => (
                        <div key={site.id} style={{ padding: '10px 14px', borderBottom: i < userDetail.sites.length - 1 ? '1px solid var(--faint)' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <span style={{ fontSize: '12px', color: 'var(--fg)' }}>{site.business_name}</span>
                            <span style={{ fontFamily: 'var(--mono)', fontSize: '9px', color: 'var(--muted)', marginLeft: '8px' }}>{site.style} · v{site.version} · {site.page_count} page{site.page_count !== 1 ? 's' : ''}</span>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {site.is_published && <span style={{ fontFamily: 'var(--mono)', fontSize: '8px', color: '#5ba89d', padding: '2px 6px', background: 'rgba(91,168,157,0.1)', border: '1px solid rgba(91,168,157,0.2)' }}>LIVE</span>}
                            {site.custom_domain?.domain && <span style={{ fontFamily: 'var(--mono)', fontSize: '8px', color: 'var(--gold)' }}>{site.custom_domain.domain}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Payment history */}
                {userDetail.payments?.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--muted)', letterSpacing: '.1em', marginBottom: '10px' }}>PAYMENT HISTORY</h3>
                    <div style={{ border: '1px solid var(--faint)' }}>
                      {userDetail.payments.map((p, i) => (
                        <div key={i} style={{ padding: '8px 14px', borderBottom: i < userDetail.payments.length - 1 ? '1px solid var(--faint)' : 'none', display: 'grid', gridTemplateColumns: '1fr 80px 80px 100px' }}>
                          <span style={{ fontSize: '11px', color: 'var(--fg)' }}>{p.plan_key}</span>
                          <span style={{ fontSize: '11px', color: 'var(--gold)', fontFamily: 'var(--mono)' }}>${p.amount}</span>
                          <span style={{ fontSize: '9px', color: p.payment_status === 'paid' ? '#5ba89d' : 'var(--muted)', fontFamily: 'var(--mono)', textTransform: 'uppercase' }}>{p.payment_status}</span>
                          <span style={{ fontSize: '9px', color: 'var(--muted)' }}>{new Date(p.created_at).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent AI calls */}
                {userDetail.ai_history?.length > 0 && (
                  <div>
                    <h3 style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--muted)', letterSpacing: '.1em', marginBottom: '10px' }}>RECENT AI CALLS (30 DAYS)</h3>
                    <div style={{ border: '1px solid var(--faint)', maxHeight: '300px', overflowY: 'auto' }}>
                      {userDetail.ai_history.slice(0, 30).map((h, i) => (
                        <div key={i} style={{ padding: '6px 14px', borderBottom: '1px solid var(--faint)', display: 'grid', gridTemplateColumns: '100px 80px 60px 80px 1fr', fontSize: '10px' }}>
                          <span style={{ color: 'var(--fg)' }}>{h.call_type}</span>
                          <span style={{ color: 'var(--muted)', fontFamily: 'var(--mono)' }}>${h.estimated_cost}</span>
                          <span style={{ color: h.success ? '#5ba89d' : '#c47272' }}>{h.success ? 'OK' : 'FAIL'}</span>
                          <span style={{ color: 'var(--muted)' }}>{h.duration_ms ? `${(h.duration_ms / 1000).toFixed(1)}s` : '—'}</span>
                          <span style={{ color: 'var(--muted)' }}>{new Date(h.timestamp).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </>
        )}

        {/* ── SITES TAB ── */}
        {tab === 'sites' && !selectedUser && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '28px' }}>
              <StatCard label="Total Sites" value={s.total_sites || 0} icon={Globe} />
              <StatCard label="Published" value={s.published_sites || 0} color="#5ba89d" />
              <StatCard label="Drafts" value={s.draft_sites || 0} />
              <StatCard label="Total Pages" value={s.total_pages || 0} />
            </div>
            {s.popular_styles?.length > 0 && (
              <div>
                <h3 style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--muted)', letterSpacing: '.1em', marginBottom: '12px' }}>STYLE DISTRIBUTION</h3>
                <SimpleBar
                  items={s.popular_styles.map(st => ({ label: st.style || '?', value: st.count, display: `${st.count}` }))}
                  maxVal={Math.max(...s.popular_styles.map(st => st.count))}
                  color="rgba(201,168,76,0.7)"
                />
              </div>
            )}
          </>
        )}

        {/* ── ALERTS TAB ── */}
        {tab === 'alerts' && !selectedUser && (
          <>
            {al.alert_count === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <Shield size={32} style={{ color: '#5ba89d', margin: '0 auto 12px' }} />
                <p style={{ fontFamily: 'var(--serif)', fontSize: '18px', marginBottom: '4px' }}>All clear</p>
                <p style={{ fontSize: '12px', color: 'var(--muted)' }}>No alerts at this time. Everything looks healthy.</p>
              </div>
            )}

            {al.cost_outliers?.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: '#c47272', letterSpacing: '.1em', marginBottom: '10px' }}>COST OUTLIERS — USERS COSTING MORE THAN THEIR PLAN</h3>
                <div style={{ border: '1px solid rgba(196,71,114,0.2)' }}>
                  {al.cost_outliers.map((o, i) => (
                    <div key={i} onClick={() => fetchUserDetail(o.user_id)} style={{ padding: '10px 14px', borderBottom: '1px solid var(--faint)', display: 'grid', gridTemplateColumns: '1fr 60px 80px 80px 40px', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(196,71,114,0.04)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ fontSize: '11px', color: 'var(--fg)' }}>{o.email}</span>
                      <span style={{ fontSize: '9px', fontFamily: 'var(--mono)', textTransform: 'uppercase', color: 'var(--muted)' }}>{o.plan}</span>
                      <span style={{ fontSize: '11px', color: '#c47272', textAlign: 'right', fontFamily: 'var(--mono)' }}>-${o.deficit}</span>
                      <span style={{ fontSize: '11px', color: 'var(--muted)', textAlign: 'right' }}>${o.ai_cost} spent</span>
                      <ChevronRight size={12} style={{ color: 'var(--muted)' }} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {al.past_due_accounts?.length > 0 && (
              <div>
                <h3 style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: '#c89632', letterSpacing: '.1em', marginBottom: '10px' }}>PAST DUE ACCOUNTS</h3>
                <div style={{ border: '1px solid rgba(200,150,50,0.2)' }}>
                  {al.past_due_accounts.map((pd, i) => (
                    <div key={i} onClick={() => fetchUserDetail(pd.id)} style={{ padding: '10px 14px', borderBottom: '1px solid var(--faint)', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(200,150,50,0.04)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ fontSize: '11px', color: 'var(--fg)' }}>{pd.email} — </span>
                      <span style={{ fontSize: '9px', fontFamily: 'var(--mono)', textTransform: 'uppercase', color: '#c89632' }}>{pd.plan}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── ACTIVITY TAB ── */}
        {tab === 'activity' && !selectedUser && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '28px' }}>
              <StatCard label="Signups (24h)" value={a.signups_24h || 0} icon={Activity} />
              <StatCard label="Signups (7d)" value={a.signups_7d || 0} />
              <StatCard label="Total Users" value={r.total_users || 0} icon={Users} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <h3 style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--muted)', letterSpacing: '.1em', marginBottom: '10px' }}>RECENT SIGNUPS</h3>
                <div style={{ border: '1px solid var(--faint)', maxHeight: '400px', overflowY: 'auto' }}>
                  {(a.recent_signups || []).map((u, i) => (
                    <div key={i} onClick={() => fetchUserDetail(u.id)} style={{ padding: '8px 14px', borderBottom: '1px solid var(--faint)', display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.04)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ fontSize: '11px', color: 'var(--fg)' }}>{u.email}</span>
                      <span style={{ fontSize: '9px', fontFamily: 'var(--mono)', color: 'var(--muted)', textTransform: 'uppercase' }}>{u.plan || 'free'}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--muted)', letterSpacing: '.1em', marginBottom: '10px' }}>RECENT PAYMENTS</h3>
                <div style={{ border: '1px solid var(--faint)', maxHeight: '400px', overflowY: 'auto' }}>
                  {(a.recent_payments || []).length === 0 ? (
                    <p style={{ padding: '20px', textAlign: 'center', fontSize: '12px', color: 'var(--muted)' }}>No payments yet</p>
                  ) : (a.recent_payments || []).map((p, i) => (
                    <div key={i} style={{ padding: '8px 14px', borderBottom: '1px solid var(--faint)', display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '11px', color: 'var(--fg)' }}>{p.plan_key}</span>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <span style={{ fontSize: '11px', color: 'var(--gold)', fontFamily: 'var(--mono)' }}>${p.amount}</span>
                        <span style={{ fontSize: '9px', color: p.payment_status === 'paid' ? '#5ba89d' : 'var(--muted)' }}>{p.payment_status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
